/**
 * EPBCS Phase 5 — Budgeted Financial Statements & Approval Workflow.
 * Tabs: Income Statement (derived), Balance Sheet (input grid), Approvals.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle, XCircle, PaperPlaneTilt, ArrowCounterClockwise } from "@phosphor-icons/react";
import BudgetGrid from "./BudgetGrid";
import { PovBar, useDims, fmtMonth, fmtNum, ExportButton } from "./povUtils";
import { useCapexPov, capexPovFields } from "./CapexSection";
import { ActionButton } from "./WorkflowActions";
import WorkflowSetupTab from "./WorkflowSetupTab";
import SlaSettingsTab from "./SlaSettingsTab";

export default function FinancialsSection({ sub }) {
  const tab = ["income", "balance", "approvals", "workflow", "sla"].includes(sub) ? sub : "income";
  return (
    <div data-testid="epbcs-financials-section">
      {tab === "income" && <IncomeStatementTab />}
      {tab === "balance" && <BudgetGrid form="fin_balance_sheet" exportReport="balance_sheet" />}
      {tab === "approvals" && <ApprovalsTab />}
      {tab === "workflow" && <WorkflowSetupTab />}
      {tab === "sla" && <SlaSettingsTab />}
    </div>
  );
}

// ------------------------------------------------------ Income statement ---
const IncomeStatementTab = () => {
  const dims = useDims();
  const [pov, setPov] = useCapexPov(dims);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!pov) return;
    setError(null);
    api.post("/epbcs/income-statement", { pov })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setError(e?.response?.data?.detail || e.message); });
  }, [pov]); // eslint-disable-line

  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="income-error">{error}</p>;

  return (
    <div className="space-y-4" data-testid="fin-income-tab">
      <PovBar testid="pov-fin_income" pov={pov} setPov={setPov} fields={capexPovFields(dims)}
        extra={<ActionButton section="financials" pov={pov} />} />
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-slate-500">
          Fully derived — consolidates Revenue and OpEx grids, contracts, workforce costs and CapEx
          depreciation across all cost centres of the selected POV. Grey months are actuals.
        </p>
        <ExportButton report="income_statement" pov={pov} />
      </div>
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="relative rounded-sm border border-slate-200 bg-white shadow-sm overflow-auto max-h-[calc(100vh-300px)]">
          <table className="text-[12px] w-full border-collapse">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.12em] uppercase text-slate-600">
                <th className="py-2.5 px-3 min-w-[210px] sticky left-0 top-0 bg-[#F1F5F9] z-30 border-b border-r border-slate-200">Line</th>
                {data.months.map((m) => (
                  <th key={m} className={`py-2.5 px-2 text-right min-w-[76px] whitespace-nowrap sticky top-0 bg-[#F1F5F9] z-20 border-b border-slate-200 ${m <= data.actuals_through ? "text-slate-400" : ""}`}>
                    {fmtMonth(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.sections.map((sec) => (
                <React.Fragment key={sec.key}>
                  <tr><td colSpan={1 + data.months.length}
                    className="pt-3 pb-1 px-3 text-[10px] tracking-[0.2em] uppercase font-bold text-[#0B1E3F]">{sec.title}</td></tr>
                  {sec.rows.map((r) => (
                    <tr key={r.code} className="border-b border-slate-100" data-testid={`is-row-${r.code}`}>
                      <td className="py-1.5 px-3 pl-6 sticky left-0 bg-white z-10 whitespace-nowrap">{r.code} — {r.name}</td>
                      {data.months.map((m) => (
                        <td key={m} className={`py-1.5 px-2 text-right whitespace-nowrap ${m <= data.actuals_through ? "text-slate-400 bg-slate-100/70" : r.values[m] ? "" : "text-slate-300"} font-tabular-nums`}>
                          {fmtNum(r.values[m] || 0)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="border-b border-slate-200 bg-slate-50 font-bold" data-testid={`is-total-${sec.key}`}>
                    <td className="py-1.5 px-3 sticky left-0 bg-slate-50 z-10">Total {sec.title}</td>
                    {data.months.map((m) => (
                      <td key={m} className="py-1.5 px-2 text-right whitespace-nowrap">{fmtNum(sec.total[m] || 0)}</td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
              <tr className="border-t-2 border-[#0B1E3F] bg-[#0B1E3F]/5 font-bold" data-testid="is-net-income">
                <td className="py-2 px-3 sticky left-0 bg-[#0B1E3F]/5 z-10 text-[#0B1E3F]">Net Income</td>
                {data.months.map((m) => (
                  <td key={m} className={`py-2 px-2 text-right whitespace-nowrap ${data.net_income[m] < 0 ? "text-red-600" : "text-[#0B1E3F]"}`}>
                    {fmtNum(data.net_income[m] || 0)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------ Approvals ----
const STATUS_STYLE = {
  draft:        "bg-slate-100 text-slate-500",
  first_pass:   "bg-gray-100 text-slate-600",
  awaiting_clarification: "bg-blue-50 text-blue-700 border border-blue-200",
  under_review: "bg-amber-50 text-amber-700 border border-amber-200",
  approved:     "bg-green-50 text-green-700 border border-green-200",
  rejected:     "bg-red-50 text-red-700 border border-red-200",
};
const STATUS_LABEL = {
  draft: "Draft", first_pass: "First Pass",
  awaiting_clarification: "Awaiting Clarification", under_review: "Under Review",
  approved: "Approved", rejected: "Rejected",
};
const SECTION_LABEL = {
  revenue: "Revenue Budget", opex: "OpEx Budget", capex: "CapEx Budget",
  workforce: "Workforce Budget", financials: "Financial Statements",
};

const ApprovalsTab = () => {
  const dims = useDims();
  const [pov, setPov] = useCapexPov(dims);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [comment, setComment] = useState({});
  const [open, setOpen] = useState(null);

  const load = () => {
    if (!pov) return;
    api.post("/epbcs/approvals/list", { pov }).then((r) => setData(r.data));
  };
  useEffect(load, [pov]); // eslint-disable-line

  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;

  const act = async (section, action) => {
    setError(null); setInfo(null);
    try {
      const { data } = await api.post("/epbcs/approvals/action",
        { pov, section, action, comment: comment[section] || "" });
      if (data.notified?.length) setInfo(`Email notification sent to: ${data.notified.join(", ")}`);
      setComment({ ...comment, [section]: "" });
      load();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const role = data?.role;
  const canSubmit = ["admin", "preparer", "alt_preparer"].includes(role);
  const canApprove = ["admin", "reviewer"].includes(role);

  return (
    <div className="space-y-4" data-testid="fin-approvals-tab">
      <PovBar testid="pov-approvals" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
      <p className="text-[11px] text-slate-500">
        Draft → Take Ownership → First Pass → (Assign for Clarification ⇄ Awaiting Clarification) →
        Promote → Under Review → Approved / Rejected (back to First Pass). Once a user takes ownership the
        form is <b>locked for other editors</b>; Under Review and Approved sections are fully locked.
        Your role: <b className="uppercase text-[#0B1E3F]">{role?.replace("_", " ")}</b>
      </p>
      {error && <p className="text-sm text-red-600" data-testid="approval-error">{error}</p>}
      {info && <p className="text-[12px] font-semibold text-green-700" data-testid="approval-notify-info">{info}</p>}
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="grid gap-3">
          {data.approvals.map((a) => (
            <div key={a.section} className="rounded-sm border border-slate-200 bg-white shadow-sm p-4" data-testid={`approval-card-${a.section}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-bold">{SECTION_LABEL[a.section]}</span>
                <span className={`px-2.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[a.status]}`}
                  data-testid={`approval-status-${a.section}`}>
                  {STATUS_LABEL[a.status]}
                </span>
                {a.owner && <span className="text-[11px] text-slate-400">owner: <b>{a.owner}</b></span>}
                {a.due_date && ["first_pass", "awaiting_clarification", "under_review"].includes(a.status) && (
                  <span data-testid={`approval-due-${a.section}`}
                    className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${a.overdue ? "bg-red-50 text-red-700 border border-red-200" : "text-slate-500"}`}>
                    {a.overdue ? "Overdue — was due" : "Due"} {a.due_date}
                  </span>
                )}
                {a.clarification_assignee && a.status === "awaiting_clarification" && (
                  <span className="text-[11px] text-blue-700">clarification: <b>{a.clarification_assignee}</b></span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <input value={comment[a.section] || ""} placeholder="Comment…"
                    data-testid={`approval-comment-${a.section}`}
                    onChange={(e) => setComment({ ...comment, [a.section]: e.target.value })}
                    className="border border-slate-200 px-2 py-1 text-[11px] rounded w-40" />
                  {a.status === "draft" && role !== "viewer" && (
                    <button onClick={() => act(a.section, "take_ownership")} data-testid={`approval-own-${a.section}`}
                      className="inline-flex items-center gap-1 bg-[#0B1E3F] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded">
                      Take Ownership
                    </button>
                  )}
                  {canSubmit && ["first_pass", "rejected"].includes(a.status) && (
                    <button onClick={() => act(a.section, "submit")} data-testid={`approval-submit-${a.section}`}
                      className="inline-flex items-center gap-1 bg-[#0B1E3F] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded">
                      <PaperPlaneTilt size={11} weight="fill" /> Promote
                    </button>
                  )}
                  {a.status === "awaiting_clarification" && (
                    <button onClick={() => act(a.section, "promote")} data-testid={`approval-return-${a.section}`}
                      className="inline-flex items-center gap-1 border border-blue-300 text-blue-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded">
                      <PaperPlaneTilt size={11} /> Promote to First Pass
                    </button>
                  )}
                  {canApprove && a.status === "under_review" && (<>
                    <button onClick={() => act(a.section, "approve")} data-testid={`approval-approve-${a.section}`}
                      className="inline-flex items-center gap-1 bg-green-700 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded">
                      <CheckCircle size={11} weight="fill" /> Approve
                    </button>
                    <button onClick={() => act(a.section, "reject")} data-testid={`approval-reject-${a.section}`}
                      className="inline-flex items-center gap-1 bg-red-700 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded">
                      <XCircle size={11} weight="fill" /> Reject
                    </button>
                  </>)}
                  {role === "admin" && ["approved", "rejected"].includes(a.status) && (
                    <button onClick={() => act(a.section, "reopen")} data-testid={`approval-reopen-${a.section}`}
                      className="inline-flex items-center gap-1 border border-slate-300 text-slate-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded">
                      <ArrowCounterClockwise size={11} /> Reopen
                    </button>
                  )}
                </div>
              </div>
              {(a.history || []).length > 0 && (
                <div className="mt-2">
                  <button onClick={() => setOpen(open === a.section ? null : a.section)}
                    data-testid={`approval-history-toggle-${a.section}`}
                    className="text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700">
                    Audit trail ({a.history.length})
                  </button>
                  {open === a.section && (
                    <ul className="mt-1 space-y-0.5">
                      {[...a.history].reverse().map((h, i) => (
                        <li key={i} className="text-[11px] text-slate-500">
                          <b className="uppercase">{h.action.replace("_", " ")}</b>
                          {h.from !== h.to && <> ({h.from} → {h.to})</>} by {h.by} · {h.at?.slice(0, 16).replace("T", " ")}
                          {h.tag && (
                            <span className="ml-1 text-[10px] font-bold text-[#C8A24B] border border-[#C8A24B]/40 px-1 py-px">
                              {h.tag}
                            </span>
                          )}
                          {h.comment && <span className="italic"> — “{h.comment}”</span>}
                          {(h.attachments || []).map((f) => (
                            <a key={f.id} href={`${api.defaults.baseURL}/epbcs/approvals/file/${f.id}`}
                              target="_blank" rel="noreferrer"
                              className="ml-1.5 text-[#0B1E3F] underline hover:text-[#C8A24B]"
                              data-testid={`attachment-${f.id}`}>
                              📎 {f.name}
                            </a>
                          ))}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
